/**
 * Turn the BE's technical run `errorMessage` into something a user can act on.
 *
 * Failure messages look like `do/{index}/{taskName}: {reason}` (e.g.
 * `do/2/createSpecialization: Bad Request`). We parse that, categorise the reason
 * heuristically (there is no structured `errorCode` in the contract yet), and the
 * UI maps the category to a friendly title/action while keeping the raw message in
 * a `<details>`. Centralised so the mapping evolves in one place.
 */
export interface ParsedRunError {
  /** The step's position in the pipeline, counted from 0 as the BE writes it. */
  stepIndex?: number;
  taskName?: string;
  reason: string;
  /**
   * The workflow raised this error itself (a guard's `raise`), so `reason` is copy its author wrote
   * for people and reads better than any category text. Errors typed by the workflow spec (a failed
   * service call, a timeout) are not authored.
   */
  authored?: boolean;
  /** The raw, unmodified message — always shown verbatim in a disclosure. */
  technical: string;
}

const TASK_PATTERN = /^do\/(\d+)\/([^:]+):\s*([\s\S]+)$/;

/**
 * The other shape the BE emits: a guard that rejects raises a Java exception whose
 * message embeds a structured problem, e.g.
 *
 *   io.serverlessworkflow.impl.WorkflowException: WorkflowError[type=…/print-design-colour-membership-missing,
 *   status=409, instance=do/2/guardMembership, title=Print colour membership is missing,
 *   detail=The PrintDesignColor record has no parent PrintDesign and …]
 *
 * `TASK_PATTERN` cannot see into that, so the whole Java blob used to become the
 * "reason" and got toasted verbatim. `title` + `detail` are the human halves, and
 * `instance` carries the same `do/{index}/{task}` coordinates.
 */
const WORKFLOW_ERROR_PATTERN = /WorkflowError\[([\s\S]*)\]\s*$/;
const SPEC_ERROR_TYPE_PREFIX = "https://serverlessworkflow.io/spec/";
const FIELD_PATTERN = /(?:^|,\s*)(type|status|instance|title|detail)=/g;

/** The BE serialises a missing title/detail as the literal `null`; that is not text to show. */
const NULLISH_FIELD_VALUES: ReadonlySet<string> = new Set(["", "null", "undefined"]);

const isPresentField = (value: string | undefined): value is string =>
  value !== undefined && !NULLISH_FIELD_VALUES.has(value.trim().toLowerCase());

const readWorkflowErrorFields = (body: string): Record<string, string> => {
  const fields: Record<string, string> = {};
  const marks = [...body.matchAll(FIELD_PATTERN)];

  marks.forEach((mark, index) => {
    const key = mark[1];
    const valueStart = (mark.index ?? 0) + mark[0].length;
    const valueEnd =
      index + 1 < marks.length ? (marks[index + 1].index ?? body.length) : body.length;

    fields[key] = body.slice(valueStart, valueEnd).trim();
  });

  return fields;
};

const parseWorkflowError = (technical: string): ParsedRunError | undefined => {
  const match = WORKFLOW_ERROR_PATTERN.exec(technical);

  if (!match) {
    return undefined;
  }

  const fields = readWorkflowErrorFields(match[1]);
  const reason = [fields.title, fields.detail].filter(isPresentField).join(" — ");

  if (!reason) {
    return undefined;
  }

  const instance = TASK_PATTERN.exec(`${fields.instance ?? ""}: x`);

  return {
    stepIndex: instance ? Number(instance[1]) : undefined,
    taskName: instance ? instance[2].trim() : undefined,
    reason,
    authored: !!fields.type && !fields.type.startsWith(SPEC_ERROR_TYPE_PREFIX),
    technical,
  };
};

/**
 * The runner refuses a workflow whose definition fails its schema before any step runs:
 * `Failed to register workflow {key}: /do/{index}/{task}: {schema error}`. The pointer after the
 * key names the offending step; a message that carries none still names the workflow.
 */
const REGISTRATION_FAILURE_PATTERN = /^failed to register workflow\b/i;
const REGISTRATION_STEP_PATTERN = /:\s*\/?do\/(\d+)\/([^:\s]+)\s*:/;

const parseRegistrationFailure = (technical: string): ParsedRunError | undefined => {
  if (!REGISTRATION_FAILURE_PATTERN.test(technical)) {
    return undefined;
  }

  const step = REGISTRATION_STEP_PATTERN.exec(technical);

  return {
    stepIndex: step ? Number(step[1]) : undefined,
    taskName: step ? step[2] : undefined,
    reason: technical,
    technical,
  };
};

export const parseRunError = (errorMessage: string): ParsedRunError => {
  const technical = errorMessage.trim();
  const structured = parseRegistrationFailure(technical) ?? parseWorkflowError(technical);

  if (structured) {
    return structured;
  }

  const match = TASK_PATTERN.exec(technical);

  if (!match) {
    return { reason: technical, technical };
  }

  return {
    stepIndex: Number(match[1]),
    taskName: match[2].trim(),
    reason: match[3].trim(),
    technical,
  };
};

export type RunErrorCategory =
  | "definition"
  | "invalid"
  | "notFound"
  | "conflict"
  | "permission"
  | "timeout"
  | "unknown";

export const categorizeRunError = (reason: string): RunErrorCategory => {
  const text = reason.toLowerCase();

  // First: a schema error words a missing property as "not found" and an invalid one as
  // "invalid", which would otherwise send the user hunting for a record or an input value.
  if (REGISTRATION_FAILURE_PATTERN.test(text.trim()) || text.includes("one and only one schema")) {
    return "definition";
  }

  if (text.includes("bad request") || text.includes("invalid") || text.includes("validation")) {
    return "invalid";
  }

  if (text.includes("not found") || text.includes("404")) {
    return "notFound";
  }

  if (text.includes("conflict") || text.includes("already exists") || text.includes("409")) {
    return "conflict";
  }

  if (
    text.includes("forbidden") ||
    text.includes("unauthor") ||
    text.includes("permission") ||
    text.includes("403") ||
    text.includes("401")
  ) {
    return "permission";
  }

  if (text.includes("timeout") || text.includes("timed out") || text.includes("504")) {
    return "timeout";
  }

  return "unknown";
};

const RETRIABLE_CATEGORIES: ReadonlySet<RunErrorCategory> = new Set<RunErrorCategory>([
  "timeout",
  "unknown",
]);

export const isRetriableCategory = (category: RunErrorCategory): boolean =>
  RETRIABLE_CATEGORIES.has(category);

/**
 * A different failure shape than everything above: `parseRunError` reads the
 * BE's business `errorMessage` off a run that finished. This models the poll
 * ITSELF dying — the status fetch came back 401 after apiClient already tried
 * a token refresh, so the run's real outcome is unknowable, not just
 * unsuccessful. Thrown by `fetchRunStatusOrPending` in `../api/workflowRuns`.
 *
 * Kept as a distinct `code`-bearing class (rather than a plain `Error`) so
 * callers can branch on it without string-matching a message — the API layer
 * (`workflowRuns.ts`) throws it untranslated (no i18n at that layer); UI
 * callers that catch it translate their own toast via `isWorkflowRunAuthError`.
 */
export const WORKFLOW_RUN_AUTH_ERROR_CODE = "WORKFLOW_RUN_AUTH_EXPIRED";

export class WorkflowRunAuthError extends Error {
  readonly code = WORKFLOW_RUN_AUTH_ERROR_CODE;

  constructor(message = "Workflow run status fetch failed: session expired (401).") {
    super(message);
    this.name = "WorkflowRunAuthError";
  }
}

export const isWorkflowRunAuthError = (error: unknown): error is WorkflowRunAuthError =>
  error instanceof WorkflowRunAuthError;

/**
 * The run was ACCEPTED and then never reported an outcome — the status endpoint
 * 404'd for the whole poll budget (the known "run id is not the record id" race),
 * kept answering 5xx, the run was still non-terminal when the deadline hit, or
 * the 2xx submit carried no run id to poll in the first place.
 *
 * This is deliberately NOT a failure. In every one of those shapes the workflow
 * is very likely to have run and the record to exist, so a caller that buckets it
 * with real errors and offers "retry" is offering to create a duplicate. Callers
 * that can show a third outcome branch on this; callers that can only succeed or
 * fail keep treating it as a throw, which is still correct.
 *
 * Thrown by `awaitRunTermination` in `../api/workflowRuns`, untranslated (no i18n
 * at that layer) — UI callers translate their own copy off `reason`.
 */
export const WORKFLOW_RUN_UNVERIFIED_ERROR_CODE = "WORKFLOW_RUN_UNVERIFIED";

export type WorkflowRunUnverifiedReason =
  /** Accepted, but the run never became readable within the poll budget. */
  | "notReadable"
  /** The status endpoint kept failing, so the outcome was never observed. */
  | "statusUnobservable"
  /** Readable and still running when the poll budget ran out. */
  | "timedOut"
  /** Accepted (2xx) with no run id in the body, so there is nothing to poll. */
  | "noRunId";

export class WorkflowRunUnverifiedError extends Error {
  readonly code = WORKFLOW_RUN_UNVERIFIED_ERROR_CODE;
  readonly reason: WorkflowRunUnverifiedReason;

  constructor(reason: WorkflowRunUnverifiedReason, message: string) {
    super(message);
    this.name = "WorkflowRunUnverifiedError";
    this.reason = reason;
  }
}

export const isWorkflowRunUnverifiedError = (error: unknown): error is WorkflowRunUnverifiedError =>
  error instanceof WorkflowRunUnverifiedError;
